import axios from 'axios'
import { buildApiUrl } from '@/api/url'
import { runtimeConfig } from '@/config/runtime'
import { bus } from '@/global/bus'

const api = axios.create({
  baseURL: buildApiUrl(),
  timeout: runtimeConfig.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    console.error('Request error:', error)
    return Promise.reject(error)
  },
)

api.interceptors.response.use(
  (response) => {
    // Check if response has errors array and handle them
    if (response.data?.errors?.length > 0) {
      const error = new Error('API Error')
      error.response = response
      error.apiErrors = response.data.errors
      return Promise.reject(error)
    }

    // Extract data from the new response format automatically
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      // Replace response.data with the extracted data
      response.data = response.data.data
    }

    return response
  },
  async (error) => {
    if (error.response && error.response.status === 401) {
      // Check if the error is due to an expired token
      try {
        const token = await attemptTokenRefresh()
        // Update the failed request's Authorization header with new token
        error.config.headers.Authorization = `Bearer ${token}`
        // Return a new promise with the retry
        return axios(error.config)
      } catch (refreshError) {
        // If token refresh fails, reject with the original error
        console.error('Error refreshing token:', refreshError)
        bus.emit('auth-logout')
        return Promise.reject(error)
      }
    } else {
      // Handle other errors - check for API errors in response
      if (error.response?.data?.errors) {
        error.apiErrors = error.response.data.errors
      }
      console.error('Response error:', error)
      return Promise.reject(error)
    }
  },
)

const attemptTokenRefresh = async () => {
  const refreshToken = localStorage.getItem('refresh_token')
  const accessToken = localStorage.getItem('auth_token')

  if (!refreshToken) {
    bus.emit('auth-logout')
    throw new Error('No refresh token available')
  }

  try {
    const response = await axios.post(
      buildApiUrl() + 'auth/refresh',
      { accessToken, refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
    )

    if (response.status === 200) {
      // Handle new response format with data property
      const responseData = response.data.data || response.data
      const { accessToken: token, refreshToken: newRefreshToken } = responseData
      localStorage.setItem('auth_token', token)
      localStorage.setItem('refresh_token', newRefreshToken)
      return token // Return the new token
    } else {
      bus.emit('auth-logout')
      throw new Error('Failed to refresh token')
    }
  } catch (error) {
    console.error('Token refresh error:', error)
    bus.emit('auth-logout')
    throw error // Re-throw to be handled by the interceptor
  }
}

export default api
